import { Controller } from "@hotwired/stimulus"

// Lightweight controller for simple questions (text, yes/no)
// Submits only the single question that changed, not the entire form
export default class extends Controller {
  static targets = ["autoSaveStatus", "versionConflictWarning"]
  static outlets = ["tax-organizer--progress"]

  static values = {
    updateUrl: String,
    questionId: String,
    taxYear: String,
    lockVersion: Number
  }

  connect() {
    this.autoSaveTimeout = null
    this.disableAutoSave = false
    this.isSaving = false
  }

  checkForConflict() {
    if (this.hasVersionConflictWarningTarget) {
      // Version conflict detected - blur active element to prevent immediate re-trigger
      document.activeElement?.blur()

      // Disable autosave briefly to give user time to see the warning
      this.disableAutoSave = true
      setTimeout(() => this.disableAutoSave = false, 3000)
    }
  }

  disconnect() {
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }
  }

  // Helper to re-enable all file inputs in this question
  enableFileInputs() {
    const fileInputs = this.element.querySelectorAll('input[type="file"]')
    fileInputs.forEach(input => {
      input.disabled = false
    })
  }

  // Auto-save functionality for simple questions
  autoSave(event) {
    // Skip if autosave is temporarily disabled (e.g., due to version conflict)
    if (this.disableAutoSave) return

    // Skip if a save is already in progress (debounce will catch subsequent changes)
    if (this.isSaving) {
      return
    }

    // Clear existing timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }

    // Validate the input field using native HTML5 validation
    // For masked inputs, use the exposed validation element
    const targetElement = event.target
    const validationElement = targetElement.maskedInputValidationElement || targetElement

    if (validationElement && !validationElement.checkValidity()) {
      // Field is invalid
      validationElement.classList.add('is-invalid')

      // For date inputs, don't show validation popup during 'change' events
      // (user is still typing in year/month/day fields)
      // Only show validation errors for other input types or on blur
      if (targetElement.type !== 'date' || event.type === 'blur') {
        validationElement.reportValidity()
      }

      // Don't proceed with auto-save
      this.updateAutoSaveStatus('Validation failed', 'text-danger')
      setTimeout(() => {
        this.updateAutoSaveStatus('Auto-save enabled', 'text-muted')
      }, 2000)
      return
    }

    // Field is valid - clear any validation error state
    validationElement.classList.remove('is-invalid')

    // Handle document-linked questions (those with conditional file uploads)
    if (targetElement.type === 'radio' && targetElement.dataset.uploadSectionId) {
      const uploadSection = document.getElementById(targetElement.dataset.uploadSectionId)

      if (uploadSection) {
        // Show upload section if "yes" is selected, hide if "no" is selected
        uploadSection.style.display = targetElement.value === 'yes' ? 'block' : 'none'

        // Skip auto-save for "yes" answers - file upload will set the answer
        // But DO save "no" answers to clear the question and any uploaded documents
        if (targetElement.value === 'yes') {
          return
        }
      }
    }

    // Update status to show saving
    this.updateAutoSaveStatus('Saving...', 'text-warning')

    // Debounce the save operation based on input type
    const isFileInput = event.target.type === 'file'
    const isDiscrete = event.target.type === 'radio' || event.target.type === 'checkbox'
    let delay = 1000 // Default for text inputs (debounce typing)
    if (isFileInput) delay = 100 // Quick for file uploads
    if (isDiscrete) delay = 0 // Immediate for radio/checkbox clicks

    this.autoSaveTimeout = setTimeout(() => {
      this.performAutoSave(event.target)
    }, delay)
  }

  // Perform the actual auto-save - only submits this single question
  async performAutoSave(targetElement) {
    try {
      // Set saving flag to prevent concurrent requests
      this.isSaving = true

      // Disable ALL file inputs in this question while save is in progress
      // This prevents race conditions when user clicks "yes" then immediately selects files
      const fileInputs = this.element.querySelectorAll('input[type="file"]')
      fileInputs.forEach(input => {
        input.disabled = true
      })

      // Show spinner on question badge
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.showQuestionSaving(this.questionIdValue)
      }

      // Build minimal FormData with only this question's data
      // Backend expects: questions[question_id][answer], questions[question_id][does_not_apply], etc.
      const formData = new FormData()

      // Get the answer value based on input type
      let answerValue = ''
      if (targetElement.type === 'file') {
        // For file uploads, set answer to "yes" and include all selected files
        answerValue = 'yes'
        // Add all files to FormData (supports both single and multiple file selection)
        if (targetElement.files && targetElement.files.length > 0) {
          Array.from(targetElement.files).forEach(file => {
            formData.append(targetElement.name, file)
          })
        }
      } else if (targetElement.type === 'radio') {
        // For radio buttons, find the checked one
        const checkedRadio = document.querySelector(`input[name="${targetElement.name}"]:checked`)
        answerValue = checkedRadio ? checkedRadio.value : ''
      } else if (targetElement.type === 'checkbox' && targetElement.dataset.questionId) {
        // This is the DNA checkbox, not an answer input
        answerValue = ''
      } else {
        answerValue = targetElement.value
      }

      // Get the actual question ID - for subquestions, use the input's data-question-id
      // For parent questions, use the frame's questionIdValue
      const actualQuestionId = targetElement.dataset.questionId || this.questionIdValue
      const actualLockVersion = targetElement.dataset.lockVersion || this.lockVersionValue

      // Get DNA checkbox state (use actualQuestionId for subquestions)
      const dnaCheckbox = this.element.querySelector(`input[type="checkbox"][data-question-id="${actualQuestionId}"]`)
      const doesNotApply = dnaCheckbox && dnaCheckbox.checked ? '1' : '0'

      // Send params in nested format expected by backend
      formData.append(`questions[${actualQuestionId}][answer]`, answerValue)
      formData.append(`questions[${actualQuestionId}][does_not_apply]`, doesNotApply)
      formData.append(`questions[${actualQuestionId}][lock_version]`, actualLockVersion)

      // Always use turbo_stream format for consistent UI updates
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })

      if (response.status === 403) {
        alert('You do not have permission to make changes to this tax organizer.')
        this.updateAutoSaveStatus('Permission denied', 'text-danger')

        // Re-enable all file inputs
        this.enableFileInputs()

        // Hide spinner on question badge
        if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
          this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
        }

        // Clear saving flag
        this.isSaving = false
        return
      }

      if (response.status === 409) {
        // Version conflict (optimistic locking failure)
        // Render the turbo stream which includes the refreshed question with warning
        const text = await response.text()

        // Wait for turbo to actually render before blurring/disabling
        const handleConflictAfterRender = () => {
          // Blur active element to prevent further changes
          document.activeElement?.blur()

          // Disable autosave temporarily so user sees the warning
          this.disableAutoSave = true
          setTimeout(() => this.disableAutoSave = false, 3000)

          // Check for conflict warning
          this.checkForConflict()

          // Remove the listener
          this.element.removeEventListener('turbo:frame-render', handleConflictAfterRender)
        }

        // Listen for the frame to finish rendering
        this.element.addEventListener('turbo:frame-render', handleConflictAfterRender, { once: true })

        // Render the turbo stream
        Turbo.renderStreamMessage(text)

        // Don't increment lock_version since save failed - keep current version
        // The turbo stream will update it from the reloaded question

        // Re-enable all file inputs
        this.enableFileInputs()

        // Hide spinner on question badge
        if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
          this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
        }

        // Clear saving flag - user needs to see warning
        this.isSaving = false
        return
      }

      // Let Turbo handle the stream response (works for both 2xx and 422 responses)
      // The server returns a turbo stream that replaces the entire frame with fresh HTML
      // including the updated lock_version, so no manual increment needed
      const text = await response.text()
      Turbo.renderStreamMessage(text)

      // Re-enable all file inputs
      this.enableFileInputs()

      // Hide spinner on question badge
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }

      // Clear saving flag
      this.isSaving = false

      if (response.ok) {
        // Check for version conflict after render completes
        // Use setTimeout to ensure DOM is updated and Stimulus targets are processed
        setTimeout(() => {
          this.checkForConflict()
        }, 0)

        // Update status
        this.updateAutoSaveStatus('Saved', 'text-success')
        this.updateLastSavedTime()

        // Clear file input values after successful upload
        const fileInputs = this.element.querySelectorAll('input[type="file"]')
        fileInputs.forEach(input => {
          input.value = ''
        })

        // Reset status after 2 seconds
        setTimeout(() => {
          this.updateAutoSaveStatus('Auto-save enabled', 'text-muted')
        }, 2000)
      } else {
        // Update status for validation error (422)
        this.updateAutoSaveStatus('Validation failed', 'text-danger')

        // Reset status after 3 seconds
        setTimeout(() => {
          this.updateAutoSaveStatus('Auto-save enabled', 'text-muted')
        }, 3000)
      }
    } catch (error) {
      console.error('Auto-save failed:', error)
      this.updateAutoSaveStatus('Save failed', 'text-danger')

      // Re-enable all file inputs
      this.enableFileInputs()

      // Hide spinner on question badge on error
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }

      // Clear saving flag
      this.isSaving = false

      // Reset status after 3 seconds
      setTimeout(() => {
        this.updateAutoSaveStatus('Auto-save enabled', 'text-muted')
      }, 3000)
    }
  }

  // Update auto-save status display
  updateAutoSaveStatus(text, className) {
    if (this.hasAutoSaveStatusTarget) {
      this.autoSaveStatusTarget.textContent = text
      this.autoSaveStatusTarget.className = `text-muted ${className}`
    }
  }

  // Update last saved time tooltip
  updateLastSavedTime() {
    if (this.hasAutoSaveStatusTarget) {
      const now = new Date()
      const timeString = now.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      })

      // Update the data attributes
      this.autoSaveStatusTarget.setAttribute('data-last-saved-at', now.getTime())
      this.autoSaveStatusTarget.setAttribute('data-title', `Last saved: ${timeString}`)

      // Recreate the tooltip with the new title
      const tooltipInstance = window.bootstrap?.Tooltip?.getInstance(this.autoSaveStatusTarget)
      if (tooltipInstance) {
        tooltipInstance.dispose()
        new window.bootstrap.Tooltip(this.autoSaveStatusTarget, {
          placement: this.autoSaveStatusTarget.getAttribute('data-placement') || 'bottom',
          title: `Last saved: ${timeString}`,
          trigger: 'hover'
        })
      }
    }
  }

}
